import React from 'react';
import ServiceTemplate from '@/components/ServiceTemplate';

const serviceData = {
  nidhi: {
    title: 'Nidhi Company Registration',
    description: 'Register your Nidhi Company for mutual benefit fund activities. Ideal for savings and loan operations among members with regulatory oversight.',
    keywords: 'Nidhi company, mutual benefit company, savings company, member deposit company, MCA Nidhi',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          A Nidhi Company is a type of Non-Banking Financial Company (NBFC) that operates on the principle of mutual 
          benefit among its members. It accepts deposits from and lends money only to its members. Nidhi Companies 
          are regulated by the Ministry of Corporate Affairs under the Companies Act 2013 and Nidhi Rules 2014.
        </p>
        <p>
          <strong>Key Features:</strong> Nidhi Companies can only deal with their members, cannot issue preference shares, 
          cannot deal in hire purchase or chit fund business, and must use "Nidhi Limited" in their name. They are 
          exempt from RBI regulations applicable to other NBFCs.
        </p>
      </div>
    ),
    
    benefits: [
      'Mutual benefit society for savings and lending',
      'Only borrows and lends to members',
      'No RBI registration required (exempt NBFC)',
      'Regulated by MCA under Companies Act',
      'Lower compliance than banks',
      'Can accept deposits up to 20 times of NOF',
      'Interest rates as per company policy',
      'Democratic governance by members',
    ],
    
    documents: [
      'PAN and Aadhaar of all directors (minimum 3)',
      'ID proof of minimum 7 members (initial)',
      'Address proof of registered office',
      'NOC from property owner',
      'Digital Signature Certificate (DSC)',
      'MOA with objects for Nidhi Company',
      'AOA with Nidhi specific provisions',
      'Declaration by directors',
    ],
    
    process: [
      'DSC and DIN for all directors',
      'Name approval with "Nidhi Limited" suffix',
      'Drafting of Nidhi-specific MOA and AOA',
      'Company incorporation through SPICe+',
      'Certificate of Incorporation',
      'Achieve 200 members within 1 year',
      'Net Owned Fund of ₹10 lakhs within 1 year',
      'File NDH-4 for Nidhi status declaration',
    ],
    
    timeline: '20-25 Working Days',
    pricing: '₹15,999 + Govt Fees',
    
    faqs: [
      {
        question: 'What is the minimum requirement for Nidhi Company?',
        answer: 'Minimum 3 directors, 7 members at incorporation. Within 1 year: minimum 200 members, Net Owned Fund (NOF) of ₹10 lakhs, and member deposits to NOF ratio of 1:20.',
      },
      {
        question: 'Can Nidhi Company accept deposits from non-members?',
        answer: 'No, Nidhi Company can only accept deposits from its members. Deposits from non-members is prohibited. Members must hold equity shares of the company.',
      },
    ],
  },
  
  iec: {
    title: 'IEC Code Registration',
    description: 'Import Export Code (IEC) registration for international trade. Mandatory 10-digit code for all import and export businesses in India.',
    keywords: 'IEC code, import export code, DGFT registration, foreign trade, import license, export license',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Import Export Code (IEC) is a 10-digit unique identification number issued by the Directorate General of 
          Foreign Trade (DGFT) under the Ministry of Commerce. It is mandatory for any person or business engaged 
          in import or export of goods and services. IEC has lifetime validity and does not require renewal.
        </p>
        <p>
          <strong>Why is IEC Important?</strong> Without IEC, customs will not allow you to import or export goods, 
          banks will not process foreign remittances, and you cannot claim export incentives from the government. 
          IEC is also required for registering on government e-commerce portals and foreign trade platforms.
        </p>
      </div>
    ),
    
    benefits: [
      'Mandatory for all import/export transactions',
      'Lifetime validity – no renewal required',
      'Quick online processing by DGFT',
      'Enables bank remittances in foreign currency',
      'Required for customs clearance',
      'Access to export incentives and schemes',
      'Can be used by all branches of the business',
      'Free modification for address/partner changes',
    ],
    
    documents: [
      'PAN Card of entity or individual',
      'Aadhaar Card of applicant',
      'Current bank account cancelled cheque or certificate',
      'Entity proof (COI, Partnership Deed, etc.)',
      'Registered office address proof',
      'Passport size photograph',
      'Digital Signature (optional, for faster processing)',
      'Mobile number and email linked with PAN',
    ],
    
    process: [
      'Document collection and verification',
      'Online application on DGFT portal',
      'Payment of application fee (₹500)',
      'Verification by DGFT system',
      'IEC certificate issued electronically',
      'IEC activated immediately for use',
    ],
    
    timeline: '3-5 Working Days',
    pricing: '₹2,999',
    
    faqs: [
      {
        question: 'Is IEC required for service exports?',
        answer: 'IEC is required for service exports if you want to receive payment in foreign currency and claim export incentives. It is also required for filing SOFTEX returns with RBI for software service exports.',
      },
      {
        question: 'What is the validity of IEC?',
        answer: 'IEC has lifetime validity and does not require renewal. However, it should be updated on DGFT portal whenever there are changes in business details like address, directors, or bank account.',
      },
      {
        question: 'Can individual get IEC?',
        answer: 'Yes, individuals, proprietorships, partnerships, LLPs, and companies can all obtain IEC. The IEC is linked to PAN, so each PAN can have only one IEC.',
      },
    ],
  },
  
  esi: {
    title: 'ESI Registration',
    description: 'Employee State Insurance registration for employee social security benefits. Mandatory for establishments with 10+ employees.',
    keywords: 'ESI registration, employee insurance, ESIC, social security, employee benefits, labor compliance',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Employee State Insurance (ESI) is a social security and health insurance scheme for Indian workers. Under 
          the ESI Act 1948, establishments with 10 or more employees must register with ESIC and provide medical, 
          sickness, maternity, disability, and dependent benefits to employees earning up to ₹21,000 per month.
        </p>
        <p>
          <strong>Contribution Structure:</strong> Employer contributes 3.25% and employee contributes 0.75% of gross 
          wages (total 4%). In return, employees and their dependents get cashless medical treatment at ESI hospitals 
          and dispensaries, plus cash benefits during sickness, maternity, and disability.
        </p>
      </div>
    ),
    
    benefits: [
      'Complete medical care for employees and dependents',
      'Sickness benefit – 70% of wages during illness',
      'Maternity benefit – full wages for 26 weeks',
      'Disablement benefit – lifelong for permanent disability',
      "Dependents' benefit – pension on death of employee",
      'Funeral expenses and rehabilitation benefits',
      'Legal compliance for eligible establishments',
      'Low cost – only 4% of wages total contribution',
    ],
    
    documents: [
      'Company registration certificate or GST',
      'PAN Card of the entity',
      'Address proof of establishment',
      'List of directors/partners with details',
      'Bank account details with cancelled cheque',
      'List of all employees with wages',
      'Date of commencement of production/business',
      'Aadhaar and bank details of employees',
    ],
    
    process: [
      'Online registration on ESIC portal',
      'Upload of establishment documents',
      'Employee details entry in ESIC system',
      'Employer code generation',
      'Employee IP numbers allotment',
      'Monthly contribution and return filing',
    ],
    
    timeline: '7-10 Working Days',
    pricing: '₹3,499',
    
    faqs: [
      {
        question: 'When is ESI registration mandatory?',
        answer: 'ESI is mandatory for establishments with 10 or more employees (20 in some states). All employees earning gross monthly wages up to ₹21,000 must be covered under ESI.',
      },
      {
        question: 'What is the ESI contribution rate?',
        answer: 'Employer contributes 3.25% and employee contributes 0.75% of gross wages, totaling 4%. The contribution must be deposited by 15th of following month.',
      },
      {
        question: 'What benefits do ESI employees get?',
        answer: 'ESI provides medical treatment for employee and family, sickness benefit (70% wages), maternity benefit (26 weeks full wages), disablement benefit, and dependents\' pension in case of death.',
      },
    ],
  },
  
  pf: {
    title: 'PF Registration',
    description: 'Provident Fund registration for employee retirement benefits. Mandatory for establishments with 20+ employees under EPF Act 1952.',
    keywords: 'PF registration, EPF, EPFO, provident fund, employee pension, retirement benefits',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Employees' Provident Fund (EPF) is a retirement savings scheme administered by EPFO under the EPF Act 1952. 
          Establishments with 20 or more employees must register and contribute to PF. Both employer and employee 
          contribute 12% of basic wages each month, building a retirement corpus for employees.
        </p>
        <p>
          <strong>Contribution Breakdown:</strong> Employee's 12% goes entirely to PF account. Employer's 12% is split 
          as 3.67% to PF, 8.33% to Pension Fund (EPS), and 0.5% to EDLI (insurance). Additionally, employer pays 
          admin charges of 0.5% for PF and 0.5% for EDLI.
        </p>
      </div>
    ),
    
    benefits: [
      'Retirement savings with tax benefits under Section 80C',
      'Employer matching contribution – doubles savings',
      'Pension scheme (EPS) – monthly pension after retirement',
      'Insurance coverage (EDLI) – up to ₹7 lakhs',
      'Partial withdrawal for housing, education, medical',
      'Transfer of PF on job change – portability through UAN',
      'Interest on PF balance (currently 8.15%)',
      'Legal compliance for eligible establishments',
    ],
    
    documents: [
      'Company registration certificate or GST',
      'PAN Card and TAN of the entity',
      'Address proof of establishment',
      'List of directors/partners with signatures',
      'Bank account details with cancelled cheque',
      'Employee list with Aadhaar, PAN, bank account',
      'Date of hiring first employee',
      'Digital Signature Certificate',
    ],
    
    process: [
      'Online registration on EPFO unified portal',
      'Establishment details submission',
      'DSC verification for employer',
      'Establishment code (PF code) allotment',
      'UAN generation for each employee',
      'Monthly contribution through ECR filing',
    ],
    
    timeline: '10-15 Working Days',
    pricing: '₹4,499',
    
    faqs: [
      {
        question: 'When is PF registration mandatory?',
        answer: 'PF is mandatory for establishments with 20 or more employees. Once covered, it remains applicable even if employee count falls below 20. Voluntary registration is available for smaller establishments.',
      },
      {
        question: 'What is the PF contribution rate?',
        answer: 'Both employer and employee contribute 12% of basic wages + DA. For establishments with &lt;20 employees or sick units, contribution can be 10%. Maximum wage ceiling is ₹15,000 for pension contribution.',
      },
      {
        question: 'Can employee withdraw PF before retirement?',
        answer: 'Partial withdrawal is allowed for specific purposes: housing (90% after 5 years), education/marriage (50% after 7 years), medical (full amount), or unemployment for 2 months (75%). Full withdrawal after 2 months of unemployment or at age 58.',
      },
    ],
  },
  
  pt: {
    title: 'Professional Tax Registration',
    description: 'State-level Professional Tax registration for employers and professionals. Mandatory in most states for businesses and self-employed individuals.',
    keywords: 'professional tax, PT registration, state tax, employment tax, PTEC, PTRC',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Professional Tax is a state-level tax imposed on professions, trades, and employment. It is deducted from 
          employee salaries by the employer and deposited with the state government. The maximum amount of Professional 
          Tax that can be levied is ₹2,500 per year (as per Constitution). Different states have different slab rates.
        </p>
        <p>
          <strong>Two Types of Registration:</strong> PTEC (Professional Tax Enrollment Certificate) is for employers 
          who deduct PT from employees. PTRC (Professional Tax Registration Certificate) is for professionals and 
          self-employed persons who pay PT on their own income.
        </p>
      </div>
    ),
    
    benefits: [
      'Legal compliance with state tax laws',
      'Employer certificate (PTEC) for payroll compliance',
      'Professional certificate (PTRC) for self-employed',
      'Tax deduction facility for employees',
      'Deductible expense for Income Tax',
      'Required for business registrations in many states',
      'Online filing and payment facility',
      'Avoid penalties for non-compliance',
    ],
    
    documents: [
      'Business registration certificate',
      'PAN Card of business/individual',
      'Address proof of establishment',
      'Employee strength details',
      'Cancelled cheque of business account',
      'Photo and ID of proprietor/partners/directors',
      'GST certificate (if registered)',
      'State-specific forms and declarations',
    ],
    
    process: [
      'Determine applicable state PT rates',
      'Online registration on state portal',
      'Document upload and verification',
      'PTEC/PTRC certificate issuance',
      'Monthly/quarterly PT deduction from salaries',
      'Filing of PT returns as per state schedule',
    ],
    
    timeline: '5-7 Working Days',
    pricing: '₹2,999',
    
    faqs: [
      {
        question: 'Which states have Professional Tax?',
        answer: 'Professional Tax is applicable in most states including Maharashtra, Karnataka, Tamil Nadu, Gujarat, West Bengal, Andhra Pradesh, Telangana, Madhya Pradesh, etc. Delhi, Haryana, UP, Rajasthan do not have PT.',
      },
      {
        question: 'What is the maximum Professional Tax amount?',
        answer: 'Maximum Professional Tax is ₹2,500 per year as per Article 276 of the Constitution. Different states have different slab structures based on salary. Most states charge ₹200 per month (₹2,400/year).',
      },
      {
        question: 'Who needs to pay Professional Tax?',
        answer: 'Salaried employees (deducted by employer), self-employed professionals (doctors, lawyers, CAs), businesses and firms, and companies must pay PT. Exemptions available for disabled persons, senior citizens, and ex-servicemen in some states.',
      },
    ],
  },
  
  msme: {
    title: 'MSME/Udyam Registration',
    description: 'MSME registration for small and medium enterprises. Unlock government benefits, subsidies, and priority sector lending with free Udyam certificate.',
    keywords: 'MSME registration, Udyam registration, small business, medium enterprise, government benefits, priority lending',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          MSME (Micro, Small and Medium Enterprises) Registration, now called Udyam Registration, is a government 
          initiative to promote and support small businesses. The registration is completely free and done online 
          on the Udyam portal. It provides access to numerous government schemes, subsidies, and priority sector lending.
        </p>
        <p>
          <strong>Classification Criteria:</strong> Businesses are classified based on investment in plant/machinery 
          and annual turnover. Micro: Investment up to ₹1 crore, Turnover up to ₹5 crore. Small: Investment up to 
          ₹10 crore, Turnover up to ₹50 crore. Medium: Investment up to ₹50 crore, Turnover up to ₹250 crore.
        </p>
      </div>
    ),
    
    benefits: [
      'Collateral-free loans from banks',
      'Credit Guarantee Fund Scheme coverage',
      'Interest rate subvention on loans',
      'Priority sector lending eligibility',
      'Government tender preference',
      'Subsidy on patent and trademark registration',
      'Technology upgradation support',
      'Market development assistance',
      'Protection against delayed payments',
      'ISO certification reimbursement',
      'GST registration exemption benefits',
      'Free registration – no government fee',
    ],
    
    documents: [
      'Aadhaar Card of proprietor/partners/directors',
      'PAN Card of business entity',
      'Business address details',
      'Bank account information',
      'NIC code (activity classification)',
      'Investment and turnover details',
      'Mobile number linked with Aadhaar',
      'Email ID for certificate delivery',
    ],
    
    process: [
      'Visit Udyam Registration portal',
      'Enter Aadhaar number and OTP verification',
      'Fill business details and NIC code',
      'Self-declaration of investment and turnover',
      'GST and PAN auto-verification (if registered)',
      'Instant generation of Udyam certificate',
    ],
    
    timeline: '1-2 Working Days',
    pricing: '₹1,999',
    
    faqs: [
      {
        question: 'What is the eligibility for MSME registration?',
        answer: 'Any business with investment up to ₹50 crore in plant/machinery and turnover up to ₹250 crore is eligible. Manufacturing, service, and trading businesses can register. Both new and existing businesses are eligible.',
      },
      {
        question: 'Is MSME registration mandatory?',
        answer: 'MSME registration is not mandatory but highly recommended. It is required to avail government schemes, subsidies, preference in tenders, and protection under MSMED Act for delayed payments.',
      },
      {
        question: 'Can existing UAM be converted to Udyam?',
        answer: 'Yes, existing UAM (Udyog Aadhaar Memorandum) must be migrated to new Udyam registration. The migration is free and done online. UAM without migration is no longer valid.',
      },
    ],
  },
  
  shop: {
    title: 'Shop & Establishment License',
    description: 'Shop and Establishment license for commercial premises. Mandatory registration under state labor laws for all shops, offices, and commercial establishments.',
    keywords: 'shop license, establishment license, labor license, commercial registration, S&E Act',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Shop and Establishment License is issued under the Shops and Establishments Act of respective states. 
          It is mandatory for all commercial establishments including shops, offices, restaurants, hotels, warehouses, 
          and entertainment venues. The license regulates working hours, holidays, wages, and employee welfare.
        </p>
        <p>
          <strong>Why is it Important?</strong> Operating without a valid Shop License is an offense punishable with 
          fine and closure. Banks require this license for opening current accounts, it's needed for GST registration 
          in some cases, and is proof of establishment for various government registrations.
        </p>
      </div>
    ),
    
    benefits: [
      'Legal recognition of business establishment',
      'Required for opening business bank account',
      'Proof of establishment for various registrations',
      'Mandatory for employing workers',
      'Regulates working hours and holidays',
      'Employee welfare protection',
      'Required for GST registration',
      'Needed for signboard and name board permission',
    ],
    
    documents: [
      'Identity proof of owner (PAN, Aadhaar)',
      'Address proof of establishment (rent/ownership)',
      'NOC from landlord (if rented)',
      'Passport size photograph',
      'Details of employees (if any)',
      'Nature of business/trade',
      'Premises layout plan',
      'Fire safety NOC (for larger establishments)',
    ],
    
    process: [
      'Application to local labor department/municipality',
      'Document submission and fee payment',
      'Verification/inspection of premises',
      'License issuance with registration number',
      'Display of license at establishment',
      'Annual renewal before expiry',
    ],
    
    timeline: '7-15 Working Days',
    pricing: '₹2,499',
    
    faqs: [
      {
        question: 'Is Shop License mandatory for all businesses?',
        answer: 'Yes, Shop and Establishment License is mandatory for all commercial establishments including shops, offices, hotels, restaurants, warehouses, and any premises where trade or business is carried on.',
      },
      {
        question: 'What is the validity of Shop License?',
        answer: 'Validity varies by state – 1 year in most states, up to 5 years in some. License must be renewed before expiry. Late renewal attracts penalty and business can be sealed for operating without valid license.',
      },
      {
        question: 'Can home-based business get Shop License?',
        answer: 'Policies vary by state and municipality. Many states allow home-based businesses with Shop License if the activity doesn\'t create nuisance. Check with local labor office for specific requirements.',
      },
    ],
  },
  
  startup: {
    title: 'Startup India Registration',
    description: 'Government recognition under Startup India for tax exemptions, funding opportunities, and simplified compliance. DPIIT certification for innovative startups.',
    keywords: 'Startup India, DPIIT recognition, startup registration, tax exemption, startup benefits, angel tax',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Startup India is a flagship government initiative to build a strong ecosystem for startups in India. 
          DPIIT (Department for Promotion of Industry and Internal Trade) recognition provides startups with tax 
          exemptions, easier compliance, funding support, and various other benefits to promote innovation and 
          entrepreneurship.
        </p>
        <p>
          <strong>Eligibility Criteria:</strong> Entity must be incorporated as Private Limited Company, LLP, or 
          Partnership Firm; working towards innovation or scalable business model; turnover should not exceed 
          ₹100 crore in any year since incorporation; should not be formed by restructuring existing business; 
          and must be within 10 years of incorporation.
        </p>
      </div>
    ),
    
    benefits: [
      'Income Tax exemption for 3 consecutive years (Section 80-IAC)',
      'Angel Tax exemption on investments',
      'Self-certification for labor and environmental laws',
      'Fast-track patent application with 80% fee rebate',
      'Easy winding up – 90 days under IBC',
      'Access to Fund of Funds (₹10,000 crore corpus)',
      'Credit Guarantee Scheme up to ₹10 crore',
      'Government tender exemption from experience/turnover criteria',
      'Networking through Startup India Hub',
      'Mentorship and incubation support',
    ],
    
    documents: [
      'Company/LLP/Partnership incorporation certificate',
      'PAN Card of the entity',
      'Brief description of business/innovation',
      'Details of innovation or unique business model',
      'Authorization letter from founders',
      'Pitch deck or business plan (optional)',
      'Website or product details (if available)',
      'Patent/trademark applications (if any)',
    ],
    
    process: [
      'Create account on Startup India portal',
      'Fill the recognition application form',
      'Describe innovation/business model clearly',
      'Upload required documents',
      'Self-certification declarations',
      'Review by DPIIT (usually within 2 days)',
      'Certificate of Recognition issued',
      'Apply for specific benefits post-recognition',
    ],
    
    timeline: '3-5 Working Days',
    pricing: '₹3,999',
    
    faqs: [
      {
        question: 'What is the eligibility for Startup India recognition?',
        answer: 'Entity must be incorporated within last 10 years, turnover under ₹100 crore in any year, working on innovation/scalable model, not formed by restructuring, and incorporated as Private Ltd, LLP, or Partnership.',
      },
      {
        question: 'What is the 3-year tax exemption?',
        answer: 'Recognized startups can claim 100% tax exemption on profits for any 3 consecutive years out of first 10 years from incorporation. Requires certification from Inter-Ministerial Board under Section 80-IAC.',
      },
      {
        question: 'How to get Angel Tax exemption?',
        answer: 'DPIIT recognized startups can get exemption from angel tax (Section 56(2)(viib)) on investments above fair market value. Form 2 is submitted to DPIIT declaring the investment and investor details.',
      },
    ],
  },
  
  directorChange: {
    title: 'Director Change/Addition',
    description: 'Add new directors, remove existing directors, or change director details in your company. Complete MCA compliance with expert support.',
    keywords: 'director appointment, director resignation, director change, DIR-12, DIN, company director',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Changes in company directors – whether appointment, resignation, removal, or change in personal details – 
          must be intimated to the Registrar of Companies (ROC) within stipulated timelines. Non-compliance attracts 
          penalties and can affect the director's DIN status.
        </p>
        <p>
          <strong>Types of Director Changes:</strong> New director appointment (requires DIN and consent), director 
          resignation (voluntary exit), director removal (by shareholders), change in director's address/designation, 
          and KYC update for existing directors. Each change has specific forms and timelines.
        </p>
      </div>
    ),
    
    benefits: [
      'Expert handling of all MCA formalities',
      'DIN application for new directors',
      'Board resolution drafting',
      'Form DIR-12 preparation and filing',
      'Seamless transition of responsibilities',
      'Updated MCA records and certificates',
      'Compliance with Companies Act timelines',
      'Post-filing support and documentation',
    ],
    
    documents: [
      'Board resolution for appointment/resignation',
      'Consent letter from new director (Form DIR-2)',
      'Resignation letter from outgoing director',
      'PAN and Aadhaar of new director',
      'Passport (for foreign directors)',
      'Address proof of new director',
      'DSC of existing director for signing',
      'Previous board resolution copies',
    ],
    
    process: [
      'Board meeting resolution for director change',
      'DIN application for new director (if not existing)',
      'Collect consent and documents from directors',
      'Prepare Form DIR-12 for ROC filing',
      'File within 30 days of board resolution',
      'ROC approval and record update',
      'Update statutory registers',
      'Provide updated MOA/AOA if required',
    ],
    
    timeline: '7-10 Working Days',
    pricing: '₹4,999',
    
    faqs: [
      {
        question: 'What is the time limit for filing director changes?',
        answer: 'Form DIR-12 must be filed within 30 days of board resolution for appointment, resignation, or change. Late filing attracts additional fee of ₹100 per day. Continuous default can lead to DIN deactivation.',
      },
      {
        question: 'Can a person become director of multiple companies?',
        answer: 'Yes, a person can be director in maximum 20 companies at a time (including directorship in 10 public companies). Same DIN is used across all companies.',
      },
      {
        question: 'What if outgoing director does not resign formally?',
        answer: 'Director can be removed by shareholders through ordinary resolution at general meeting. In case of director not attending any board meeting for 12 months, vacation of office occurs automatically.',
      },
    ],
  },
  
  addressChange: {
    title: 'Company Address Change',
    description: 'Change registered office address of your company – within city, to another city, or to another state. Complete MCA compliance with expert handling.',
    keywords: 'company address change, registered office change, INC-22, company relocation, state transfer',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Companies can change their registered office address for business expansion, cost optimization, or 
          operational reasons. The procedure varies based on whether the change is within the same city, to 
          another city in same state, or to a different state altogether.
        </p>
        <p>
          <strong>Three Types of Address Change:</strong> Change within same ROC jurisdiction (simplest, Form INC-22), 
          change to different ROC within same state (requires regional director approval), change to different state 
          (requires NCLT approval and is most complex).
        </p>
      </div>
    ),
    
    benefits: [
      'Expert guidance on correct procedure',
      'Board resolution drafting',
      'Form INC-22/MGT-14 preparation',
      'Regional Director/NCLT petition if required',
      'Updated Certificate of Incorporation',
      'Coordination with creditors if needed',
      'PAN/TAN address update',
      'Bank and statutory record updates',
    ],
    
    documents: [
      'New address proof (rent agreement/sale deed)',
      'NOC from new premises owner',
      'Utility bill of new address',
      'Board resolution for address change',
      'Special resolution (for different state)',
      'Creditors NOC (for state change)',
      'Employee NOC (for state change)',
      'DSC of authorized director',
    ],
    
    process: [
      'Board meeting resolution for address change',
      'Member/shareholder approval (if state change)',
      'Prepare Form INC-22 for same jurisdiction',
      'Apply to Regional Director for different city',
      'File petition with NCLT for state change',
      'Obtain approval and file with ROC',
      'Updated COI with new address',
      'Update all statutory records',
    ],
    
    timeline: '15-20 Working Days (45-60 for state change)',
    pricing: '₹5,999 (₹15,999 for state change)',
    
    faqs: [
      {
        question: 'What is the procedure for changing address within same city?',
        answer: 'For change within same ROC jurisdiction: Board resolution, Form INC-22 filing within 30 days with new address proof. No shareholder approval needed. COI is updated automatically.',
      },
      {
        question: 'How to change registered office to another state?',
        answer: 'State change requires: Special resolution (75% majority), application to Regional Director, creditor and employee intimation, petition to NCLT, approval order, and filing with both old and new ROC.',
      },
      {
        question: 'Is MOA amendment required for address change?',
        answer: 'MOA amendment is required only for change of state as registered office state is mentioned in MOA. For address change within same state, only AOA update (if address is mentioned) and Form INC-22 is sufficient.',
      },
    ],
  },
  
  moa: {
    title: 'MOA/AOA Amendment',
    description: 'Amend Memorandum or Articles of Association for business expansion, object change, capital modification, or other corporate changes.',
    keywords: 'MOA amendment, AOA amendment, object clause change, company name change, capital clause',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Memorandum of Association (MOA) and Articles of Association (AOA) are the constitution of a company. 
          Any change in company's name, objects, capital structure, or governance rules requires formal amendment 
          through shareholder approval and ROC filing.
        </p>
        <p>
          <strong>When is Amendment Required?</strong> Change of company name, addition/deletion of business objects, 
          increase in authorized capital, change in liability clause, alteration in share rights, change in voting 
          rights, or any modification to existing articles.
        </p>
      </div>
    ),
    
    benefits: [
      'Business expansion through object addition',
      'Company name change for rebranding',
      'Capital modification for fundraising',
      'Share rights alteration',
      'Governance rule changes',
      'Expert drafting of amended clauses',
      'Form MGT-14 preparation and filing',
      'Updated certified copies',
    ],
    
    documents: [
      'Current MOA and AOA copies',
      'Board resolution for proposed amendment',
      'Special resolution from shareholders',
      'Draft of proposed amendments',
      'RD approval letter (if required)',
      'Stamp duty payment proof',
      'DSC of authorized signatory',
      'NOC from secured creditors (if any)',
    ],
    
    process: [
      'Board meeting to propose amendment',
      'Draft amended MOA/AOA clauses',
      'Special resolution at EGM/AGM',
      'File Form MGT-14 within 30 days',
      'Additional approval (for name/state change)',
      'Pay stamp duty on amended document',
      'ROC approval and updated MOA/AOA',
      'Obtain certified copies',
    ],
    
    timeline: '20-30 Working Days',
    pricing: '₹7,999',
    
    faqs: [
      {
        question: 'What majority is required for MOA/AOA amendment?',
        answer: 'Special Resolution requiring 75% majority of voting shareholders is needed for most amendments. Certain changes like shifting registered office to another state require additional approvals.',
      },
      {
        question: 'Can company change its main objects?',
        answer: 'Yes, company can add, delete, or modify object clauses through special resolution. Objects determine what business the company can undertake. Amendment allows diversification into new areas.',
      },
      {
        question: 'Is stamp duty payable on MOA/AOA amendment?',
        answer: 'Yes, stamp duty is payable on the amended MOA/AOA. The rate varies by state, typically ranging from ₹100 to ₹500 for simple amendments, and percentage basis for capital increase.',
      },
    ],
  },
  
  capital: {
    title: 'Increase Authorized Capital',
    description: 'Increase authorized share capital of your company to facilitate business growth, fundraising, and expansion. Complete MCA compliance with stamp duty assistance.',
    keywords: 'authorized capital increase, share capital, Form SH-7, capital expansion, equity increase',
    
    detailedDescription: (
      <div className="space-y-4">
        <p>
          Authorized Capital is the maximum capital a company can raise by issuing shares, as mentioned in the MOA. 
          When you need to issue more shares than the authorized limit – for fundraising, expansion, or partner 
          addition – you must first increase the authorized capital through a formal amendment process.
        </p>
        <p>
          <strong>Why Increase Authorized Capital?</strong> To raise fresh equity from investors, convert debt to 
          equity, issue ESOP shares to employees, bring in new shareholders/partners, or for any business requiring 
          more share capital. Government fee and stamp duty increase with the capital amount.
        </p>
      </div>
    ),
    
    benefits: [
      'Enable fresh equity fundraising',
      'Bring in new shareholders/investors',
      'Issue shares to employees (ESOP)',
      'Convert debt to equity',
      'Business expansion and growth',
      'Expert handling of MCA compliances',
      'Stamp duty calculation and payment support',
      'Updated MOA with increased capital',
    ],
    
    documents: [
      'Current MOA and AOA',
      'Board resolution proposing increase',
      'Ordinary resolution from shareholders',
      'Calculation of stamp duty (state-specific)',
      'Stamp duty payment challan',
      'Form SH-7 for ROC filing',
      'DSC of authorized signatory',
      'Amended capital clause draft',
    ],
    
    process: [
      'Board meeting to propose capital increase',
      'Determine new authorized capital amount',
      'Calculate government fee and stamp duty',
      'Shareholder approval through ordinary resolution',
      'Pay stamp duty (varies by state)',
      'File Form SH-7 within 30 days of resolution',
      'ROC approval and updated records',
      'File amended MOA clause',
    ],
    
    timeline: '15-20 Working Days',
    pricing: '₹6,999 + Stamp Duty',
    
    faqs: [
      {
        question: 'What is the stamp duty for capital increase?',
        answer: 'Stamp duty varies by state, typically 0.1% to 0.5% of the increase amount. For example, Maharashtra charges 0.15%, Karnataka 0.1%, Delhi 0.1%. Our team calculates exact amount based on your state.',
      },
      {
        question: 'What is the difference between Authorized and Paid-up Capital?',
        answer: 'Authorized Capital is the maximum limit of shares you can issue (ceiling). Paid-up Capital is actually issued and paid shares. You can increase paid-up only up to authorized limit, hence increase authorized first.',
      },
      {
        question: 'Can capital be increased beyond any limit?',
        answer: 'There is no upper limit on authorized capital increase. However, government fee and stamp duty increase proportionally. Plan your capital increase strategically to avoid multiple filings.',
      },
    ],
  },
};

const NidhiCompany = () => <ServiceTemplate {...serviceData.nidhi} />;
const IEC = () => <ServiceTemplate {...serviceData.iec} />;
const ESI = () => <ServiceTemplate {...serviceData.esi} />;
const PF = () => <ServiceTemplate {...serviceData.pf} />;
const PT = () => <ServiceTemplate {...serviceData.pt} />;
const MSME = () => <ServiceTemplate {...serviceData.msme} />;
const ShopEstablishment = () => <ServiceTemplate {...serviceData.shop} />;
const StartupIndia = () => <ServiceTemplate {...serviceData.startup} />;
const DirectorChange = () => <ServiceTemplate {...serviceData.directorChange} />;
const AddressChange = () => <ServiceTemplate {...serviceData.addressChange} />;
const MOAAmendment = () => <ServiceTemplate {...serviceData.moa} />;
const IncreaseCapital = () => <ServiceTemplate {...serviceData.capital} />;

export { NidhiCompany, IEC, ESI, PF, PT, MSME, ShopEstablishment, StartupIndia, DirectorChange, AddressChange, MOAAmendment, IncreaseCapital };
